/**
 * usePWAInstall — Hook für PWA-Install-Flow.
 *
 * Spec: docs/design-spec.md §8 (Mobile/PWA)
 *  - Web-Push + Install-Prompt (nach 2 Sessions)
 *
 * Funktionsweise:
 *   1. Hört auf das `beforeinstallprompt`-Event und merkt sich den Prompt.
 *   2. Zählt App-Sessions in localStorage hoch (`paypilot.session_count`),
 *      genau einmal pro Browser-Tab-Lifetime (sessionStorage-Guard).
 *   3. `shouldShowPrompt` ist true wenn:
 *      - `beforeinstallprompt` gefeuert wurde (also installierbar),
 *      - Session-Count >= 2,
 *      - User hat den Prompt nicht schon dismissed (`paypilot.install_dismissed`),
 *      - App ist nicht bereits installiert (display-mode: standalone).
 *   4. `promptInstall()` triggert das native Browser-Install-UI.
 *   5. `dismiss()` markiert dauerhaft als abgelehnt.
 *
 * Browser-Support:
 *   - Chromium (Desktop + Android): vollständig.
 *   - iOS Safari: kein `beforeinstallprompt` — `isIOS` wird zurückgegeben,
 *     UI kann eigene Anleitung anzeigen ("Teilen → Zum Home-Bildschirm").
 *   - Firefox: kein Auto-Install, Prompt erscheint nicht.
 */
import { useCallback, useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const SESSION_COUNT_KEY = "paypilot.session_count";
const SESSION_COUNTED_KEY = "paypilot.session_counted_this_tab";
const DISMISSED_KEY = "paypilot.install_dismissed";
const MIN_SESSIONS = 2;

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari sets navigator.standalone, Chrome/Edge use display-mode
  // Cast to unknown first because navigator.standalone is non-standard
  if ((navigator as unknown as { standalone?: boolean }).standalone) return true;
  return window.matchMedia?.("(display-mode: standalone)").matches ?? false;
}

function isIOSDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !/MSStream/.test(navigator.userAgent);
}

function bumpSessionCount(): number {
  if (typeof window === "undefined") return 0;
  // Pro Tab nur einmal hochzählen — sonst zählt ein Reload mehrfach.
  if (sessionStorage.getItem(SESSION_COUNTED_KEY)) {
    return parseInt(localStorage.getItem(SESSION_COUNT_KEY) ?? "0", 10) || 0;
  }
  const current = parseInt(localStorage.getItem(SESSION_COUNT_KEY) ?? "0", 10) || 0;
  const next = current + 1;
  localStorage.setItem(SESSION_COUNT_KEY, String(next));
  sessionStorage.setItem(SESSION_COUNTED_KEY, "1");
  return next;
}

export interface UsePWAInstallResult {
  /** True wenn ein Install-Prompt sinnvoll/möglich ist. */
  shouldShowPrompt: boolean;
  /** True auf iOS (kein nativer Prompt — UI braucht Anleitung). */
  isIOS: boolean;
  /** True wenn die App bereits installiert läuft. */
  isInstalled: boolean;
  /** Aktuelle Session-Anzahl (1-basiert). */
  sessionCount: number;
  /** Triggert den nativen Install-Prompt. Liefert true bei "accepted". */
  promptInstall: () => Promise<boolean>;
  /** Markiert Prompt dauerhaft als abgelehnt. */
  dismiss: () => void;
}

export function usePWAInstall(): UsePWAInstallResult {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(DISMISSED_KEY) === "1";
  });
  const [sessionCount, setSessionCount] = useState<number>(0);
  const ios = isIOSDevice();

  // Einmal beim Mount Session zählen
  useEffect(() => {
    setSessionCount(bumpSessionCount());
  }, []);

  // beforeinstallprompt abfangen
  useEffect(() => {
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    }
    function onAppInstalled() {
      setInstalled(true);
      setDeferredPrompt(null);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (choice.outcome === "accepted") {
        setInstalled(true);
        return true;
      }
      // User hat abgelehnt — als dismissed markieren, damit wir nicht nerven
      localStorage.setItem(DISMISSED_KEY, "1");
      setDismissed(true);
      return false;
    } catch {
      return false;
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  }, []);

  // iOS: kein deferredPrompt, aber wir können trotzdem die Install-Anleitung zeigen
  const installable = ios ? true : Boolean(deferredPrompt);
  const shouldShowPrompt =
    installable && !installed && !dismissed && sessionCount >= MIN_SESSIONS;

  return {
    shouldShowPrompt,
    isIOS: ios,
    isInstalled: installed,
    sessionCount,
    promptInstall,
    dismiss,
  };
}
